import React, { useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import GqlClientContext from 'contexts/GqlClientContext';
import { CardSkeleton } from 'components/Skeleton';
import search from 'hooks/grids/search';
import useFilterInput from 'hooks/useFilterInput';
import useNetworkingEffect from 'hooks/useNetworkingEffect';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';

import buildSteps, { LeaderboardStep } from './buildSteps';
import computeDynamicRanks from './computeDynamicRanks';
import DriverLeaderboardStage from './DriverLeaderboardStage';
import resolveColumns, {
  DYNAMIC_RANK_FIELD,
  getOrderedFields,
} from './resolveColumns';
import resolveViews from './resolveViews';
import useBuildLeaderboardDateScope from './useBuildLeaderboardDateScope';
import useDriverLeaderboardCells, {
  getRawValue,
} from './useDriverLeaderboardCells';
import useLeaderboardRotation from './useLeaderboardRotation';
import {
  LeaderboardDesignColumns,
  LeaderboardDesignRow,
} from './DriverLeaderboardTable';

// Trim the dataset's base view down to the configured fields (in the configured
// column order), applying per-field aliases. Mirrors SimpleGrid's
// createBaseViewOverride.
const createBaseViewOverride = ({
  slide,
  baseView,
}: {
  slide: DriverLeaderboardSlide;
  baseView: FleetOps.BaseView;
}): FleetOps.BaseView => {
  const aliasByField = new Map(
    (slide.fieldAliases ?? []).map((a) => [a.field, a.alias]),
  );
  const newFields = {} as {
    [field: string]: FleetOps.BaseViewField | undefined;
  };
  const orderedFields = getOrderedFields(slide);
  orderedFields.forEach((field) => {
    const base = baseView.fields[field];
    if (base) {
      newFields[field] = {
        ...base,
        nameAlias: aliasByField.get(field) ?? _.startCase(base.field),
      };
    }
  });

  return {
    ...baseView,
    fields: newFields,
    fieldOrder: orderedFields.filter((field) => !!newFields[field]),
  };
};

const hasCompareRange = (view: DriverLeaderboardDateView): boolean =>
  !!(
    view.compareRelativeDateRange ||
    view.compareDateRange ||
    view.compareAdvancedRelativeDateRange
  );

// A rank column's value as a number (e.g. "2" for joint second), if it is one.
const toRankNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

interface ViewDocuments {
  main: ElasticDocument[];
  compare?: ElasticDocument[];
}

const DriverLeaderboardContent = ({
  slide,
  parentBaseView,
  pinnedStepIndex,
  onStepsResolved,
}: {
  slide: DriverLeaderboardSlide;
  parentBaseView: FleetOps.BaseView;
  // Wallboard screenshot renderer: hold the rotation on one step, and report
  // the steps once the data has loaded so it can capture each one.
  pinnedStepIndex?: number;
  onStepsResolved?: (steps: LeaderboardStep[]) => void;
}) => {
  const { client } = useContext(GqlClientContext);
  const { filterInput } = useFilterInput({});
  const platesToFilterInput = usePlatesToFilterInput();
  const buildDateScope = useBuildLeaderboardDateScope();

  // The slide's own filter plates, applied to every range (main and compare).
  const filters = useMemo((): FilterInput[] => {
    if (!slide.scope || slide.scope.length === 0) {
      return [filterInput];
    }
    return [filterInput, platesToFilterInput(slide.scope)];
  }, [filterInput, platesToFilterInput, slide.scope]);

  const views = useMemo(() => resolveViews(slide), [slide]);

  const {
    columns: resolvedColumns,
    rankField,
    isDynamicRank,
  } = useMemo(() => resolveColumns(slide), [slide]);

  // Sort fields needn't be grid columns, but they're fetched anyway: the
  // dynamic rank is computed from their values.
  const includeFields = useMemo(
    () => [
      ...slide.fields,
      ...slide.sort
        .map((s) => s.field)
        .filter((f) => !slide.fields.includes(f)),
    ],
    [slide.sort, slide.fields],
  );
  // Identifies a driver across periods for Rank Change (document ids differ
  // per period): the driver name column, else the first non-rank column.
  const identityField =
    resolvedColumns.find((c) => c.display === 'name')?.field ??
    resolvedColumns.find((c) => c.display !== 'rank')?.field;
  const pageSize = Math.max(1, slide.pageSize ?? slide.topN);

  // Main + optional compare dateScope per view. Pure buildDateRange (no ambient
  // date context needed).
  const scopes = useMemo(
    () =>
      views.map((view) => ({
        main: buildDateScope(view),
        compare: hasCompareRange(view)
          ? buildDateScope({
              relativeDateRange: view.compareRelativeDateRange,
              dateRange: view.compareDateRange,
              advancedRelativeDateRange: view.compareAdvancedRelativeDateRange,
              dateField: view.compareDateField ?? view.dateField,
            })
          : undefined,
      })),
    [views, buildDateScope],
  );

  const baseView = useMemo(
    () => createBaseViewOverride({ slide, baseView: parentBaseView }),
    [slide, parentBaseView],
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loaded, setLoaded] = useState<ViewDocuments[]>([]);
  const [updatedAt, setUpdatedAt] = useState<number | undefined>();

  // Eager-load every range up front (main + compare), in parallel, and hold all
  // results so rotation swaps between them with no refetch.
  useNetworkingEffect(() => {
    let isActive = true;
    setIsLoading(true);

    const run = async () => {
      const common = {
        dataType: slide.dataType,
        filters,
        sortBy: slide.sort,
        size: slide.topN,
        includeFields,
        client,
      };
      try {
        const tasks: Promise<{
          i: number;
          isCompare: boolean;
          docs: ElasticDocument[];
        }>[] = [];
        scopes.forEach((scope, i) => {
          tasks.push(
            search({ ...common, dateScope: scope.main }).then((r) => ({
              i,
              isCompare: false,
              docs: r.documents ?? [],
            })),
          );
          if (scope.compare) {
            tasks.push(
              search({ ...common, dateScope: scope.compare }).then((r) => ({
                i,
                isCompare: true,
                docs: r.documents ?? [],
              })),
            );
          }
        });
        const results = await Promise.all(tasks);
        if (!isActive) {
          return;
        }
        const byView: ViewDocuments[] = scopes.map(() => ({ main: [] }));
        results.forEach(({ i, isCompare, docs }) => {
          if (isCompare) {
            byView[i].compare = docs;
          } else {
            byView[i].main = docs;
          }
        });
        setLoaded(byView);
        setUpdatedAt(Date.now());
      } catch (e) {
        if (isActive) {
          setLoaded(scopes.map(() => ({ main: [] })));
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      isActive = false;
    };
  }, [
    client,
    slide.dataType,
    slide.sort,
    slide.topN,
    includeFields,
    filters,
    scopes,
  ]);

  // Every page of every range, in rotation order.
  const steps = useMemo(
    () =>
      buildSteps({
        views,
        documentCounts: views.map((_v, i) => loaded[i]?.main.length ?? 0),
        pageSize,
      }),
    [views, loaded, pageSize],
  );
  const { index, progress, isExiting } = useLeaderboardRotation({
    steps,
    pinnedIndex: pinnedStepIndex,
  });
  useEffect(() => {
    if (!isLoading && onStepsResolved) {
      onStepsResolved(steps);
    }
  }, [isLoading, onStepsResolved, steps]);
  const step = steps[Math.min(index, steps.length - 1)];
  const activeIndex = step.viewIndex;
  const active = loaded[activeIndex] ?? { main: [] };
  const hasRankChange = !!scopes[activeIndex]?.compare;
  const pageOffset = step.pageIndex * pageSize;
  const pageDocuments = useMemo(
    () => active.main.slice(pageOffset, pageOffset + pageSize),
    [active.main, pageOffset, pageSize],
  );

  // Generic formatted cells for the active page (formatting flows through the
  // base view), in the configured column order.
  const { columns: genColumns, rows: genRows } = useDriverLeaderboardCells({
    baseView,
    documents: pageDocuments,
    dataType: slide.dataType,
  });

  // Dynamic ranks over the whole range (not just the page), aligned with the
  // range's documents.
  const dynamicRanks = useMemo(
    () =>
      isDynamicRank
        ? computeDynamicRanks({ documents: active.main, sortBy: slide.sort })
        : undefined,
    [isDynamicRank, slide.sort, active.main],
  );
  const compareDynamicRanks = useMemo(
    () =>
      isDynamicRank && active.compare
        ? computeDynamicRanks({ documents: active.compare, sortBy: slide.sort })
        : undefined,
    [isDynamicRank, slide.sort, active.compare],
  );

  // Prior-period rank by driver identity. Uses the dynamic rank or the rank
  // column's value when configured (so ties match what's displayed), else the
  // position.
  const compareRankByIdentity = useMemo(() => {
    const map = new Map<string, number>();
    if (!identityField) {
      return map;
    }
    (active.compare ?? []).forEach((doc, idx) => {
      const key = String(
        getRawValue({ field: identityField, document: doc }) ?? '',
      );
      const rank = compareDynamicRanks
        ? compareDynamicRanks[idx]
        : rankField
          ? toRankNumber(getRawValue({ field: rankField, document: doc }))
          : idx + 1;
      if (key && rank !== undefined && !map.has(key)) {
        map.set(key, rank);
      }
    });
    return map;
  }, [active.compare, compareDynamicRanks, identityField, rankField]);

  const columns: LeaderboardDesignColumns = useMemo(() => {
    const byField = new Map(genColumns.map((c) => [c.field, c]));
    return {
      columns: resolvedColumns.map(({ field, display }) =>
        field === DYNAMIC_RANK_FIELD
          ? { field, display, label: 'Rank', isLeftAlign: false }
          : {
              field,
              display,
              label: byField.get(field)?.label ?? _.startCase(field),
              isLeftAlign: byField.get(field)?.isLeftAlign ?? false,
            },
      ),
      hasRankChange,
    };
  }, [genColumns, resolvedColumns, hasRankChange]);

  const rows: LeaderboardDesignRow[] = useMemo(
    () =>
      genRows.map((row, i) => {
        const byField = new Map(row.cells.map((c) => [c.field, c]));
        const rankValue = dynamicRanks
          ? dynamicRanks[pageOffset + i]
          : rankField
            ? toRankNumber(byField.get(rankField)?.rawValue)
            : undefined;
        let rankChange: number | 'new' | null = null;
        if (hasRankChange) {
          const rank =
            dynamicRanks || rankField ? rankValue : pageOffset + i + 1;
          const identity = identityField
            ? String(byField.get(identityField)?.rawValue ?? '')
            : '';
          const prior = compareRankByIdentity.get(identity);
          rankChange =
            prior === undefined || rank === undefined ? 'new' : prior - rank;
        }
        return {
          key: row.key,
          rankValue,
          rankChange,
          cells: resolvedColumns.map(({ field }) => ({
            field,
            value:
              field === DYNAMIC_RANK_FIELD
                ? rankValue ?? ''
                : byField.get(field)?.formatted ?? '',
          })),
        };
      }),
    [
      genRows,
      dynamicRanks,
      rankField,
      identityField,
      resolvedColumns,
      hasRankChange,
      pageOffset,
      compareRankByIdentity,
    ],
  );

  if (isLoading && loaded.length === 0) {
    return <CardSkeleton />;
  }

  return (
    <DriverLeaderboardStage
      slide={slide}
      views={views}
      steps={steps}
      stepIndex={index}
      progress={progress}
      isExiting={isExiting}
      columns={columns}
      rows={rows}
      // Paged: reserve page-size rows so a short last page keeps row height.
      // Single page: rows stretch to fill (0 = no reserved slots).
      rowSlots={step.pageCount > 1 ? pageSize : 0}
      updatedAt={updatedAt}
    />
  );
};

export default DriverLeaderboardContent;
